import React, { Suspense, lazy, useState } from "react";
import "../assets/scss/MusicCard.scss";
import { AiFillPlayCircle } from "react-icons/ai";
import { FiHeadphones, FiCheck, FiYoutube } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import {
    setCurrentMargin,
    setCurrentPlaying,
    setNoInteractionCount,
    setPlayPauseStatus
} from "../../actions/actions";
import { hasReachedListeningMastery, LISTENING_MASTERY_REQUIRED_PLAYS } from "../../constants/listeningProgress";
import { getTrackVideoSegment } from "../../constants/workbookVideoChapters";
// 影片播放器（含 YouTube 程式）按下「看影片」才載入。
const LessonVideoDialog = lazy(() => import("./LessonVideoDialog"));

function MusicCard({
    music,
    playbackQueue = [],
    progress = {},
    index = 0,
    progressStatus = "ready",
    onStart,
    assignmentProgress,
    assignmentProgressStatus = "ready",
    hideVideo = false,
    videoContext = "playlist"
}) {
    const dispatch = useDispatch();

    const {
        page,
        audioURL
    } = music || {};

    const playCount =
        Number(
            progress?.playCount ??
            progress?.play_count ??
            0
        ) || 0;

    const completed = progressStatus === "ready" && hasReachedListeningMastery(progress);
    // 教學影片在平台裡播（不跳到 YouTube），只播這一頁的段落。
    const videoSegment = getTrackVideoSegment(music?.type, page);
    const [videoOpen, setVideoOpen] = useState(false);
    // The page already names the book, so the card shows just the track ("P22").
    const bookName = String(music?.bookname || "").trim();
    const shortTitle = raw => bookName && String(raw).startsWith(bookName) ? String(raw).slice(bookName.length).trim() || raw : raw;
    const title = music?.title || music?.music_name || music?.musicName || page || "教材音檔";

    const currentPlaying =
        useSelector(
            state =>
                state.musicReducer.playing
        );

    const playingStatus =
        useSelector(
            state =>
                state.musicReducer.playingStatus
        );

    const isCurrentTrack =
        Boolean(
            currentPlaying &&
            currentPlaying.id === music?.id
        );

    const isPlaying =
        isCurrentTrack &&
        playingStatus;

    const handlePlay = () => {
        if (!audioURL) {
            console.error(
                "找不到音檔網址:",
                music
            );
            return;
        }

        if (!isPlaying) onStart?.(music);

        dispatch(
            setCurrentMargin(
                "100px"
            )
        );

        dispatch(
            setNoInteractionCount(
                0
            )
        );

        localStorage.setItem(
            "ae-no-interaction",
            "0"
        );

        if (isCurrentTrack) {
            dispatch(
                setPlayPauseStatus(
                    !playingStatus
                )
            );
            return;
        }

        dispatch(
            setCurrentPlaying({
                ...music,
                audioURL,
                playbackQueue
            })
        );

        dispatch(
            setPlayPauseStatus(
                true
            )
        );
    };

    return (
        <div
            className={[
                "music-card",
                assignmentProgress ? "music-card--assignment" : "",
                isCurrentTrack
                    ? "music-card--active"
                    : "",
                (assignmentProgress ? assignmentProgress.completed : completed)
                    ? "music-card--completed"
                    : "",
                `music-card--theme-${index % 5}`
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <button
                type="button"
                className="music-card__play"
                onClick={handlePlay}
                disabled={!audioURL}
                aria-label={`${audioURL ? isPlaying ? "暫停" : "播放" : "音檔暫時無法播放"} ${title}`}
            >
                <span className="music-card__play-ring" aria-hidden="true" />
                {isPlaying ? (
                    <span className="music-card__equalizer" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                        <span />
                    </span>
                ) : (
                    <AiFillPlayCircle aria-hidden="true" />
                )}
                <span className="music-card__play-label">{isPlaying ? "暫停" : "播放"}</span>
            </button>

            <div className="music-card__info">
                <div className="music-card__title">
                    {shortTitle(title)}
                </div>

                {isCurrentTrack && <span className="music-card__current">{isPlaying ? "正在聆聽" : "已暫停，可繼續聽"}</span>}
                {!audioURL && <span className="music-card__unavailable">音檔暫時無法播放</span>}
            </div>

            <div className={`music-card__status${assignmentProgress ? " has-assignment" : ""}`}>
                {assignmentProgress && <div className="music-card__assignment"><strong>{assignmentProgress.completed ? "本次作業已完成" : "本次作業"}</strong><span>{assignmentProgressStatus !== "ready" ? "作業進度尚未確認" : `已聽 ${assignmentProgress.playCount} 次，還需 ${Math.max(0, assignmentProgress.requiredListens - assignmentProgress.playCount)} 次`}</span><b className="music-card__count" aria-hidden="true">{assignmentProgressStatus === "ready" ? `${Math.min(assignmentProgress.playCount, assignmentProgress.requiredListens)} / ${assignmentProgress.requiredListens} 次` : "—"}</b><small>老師指定：{assignmentProgress.requiredListens} 次</small></div>}
                {videoSegment && !hideVideo && <button type="button" className="music-card__video" onClick={() => setVideoOpen(true)} aria-label={`看 ${title} 的教學影片`}><FiYoutube aria-hidden="true" /><span>看影片</span></button>}
                {videoOpen && videoSegment && <Suspense fallback={null}><LessonVideoDialog segment={videoSegment} title={shortTitle(title)} trackId={Number(music?.id) || undefined} context={videoContext} onClose={() => setVideoOpen(false)} /></Suspense>}
                <div className="music-card__plays">
                    <FiHeadphones aria-hidden="true" />
                    <span><small>{assignmentProgress ? "長期熟練累積" : "熟練進度"}</small><strong>{progressStatus === "ready" ? `${Math.min(playCount, LISTENING_MASTERY_REQUIRED_PLAYS)} / ${LISTENING_MASTERY_REQUIRED_PLAYS}` : progressStatus === "loading" ? "讀取中" : "紀錄未載入"}</strong></span>
                </div>

                {completed && (
                    <div
                        className="music-card__check"
                        title="已通過"
                    >
                        <FiCheck aria-hidden="true" />
                        <span>{assignmentProgress ? "長期熟練達標" : "通過"}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MusicCard;
